import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { UsersRound, Radio, Unplug, Wifi, RadioTower, Router, ArrowUpRight, Receipt, CalendarClock, Ban, IdCard, Printer, LogIn, CalendarX2 } from 'lucide-react';

/**
 * 3 big cards displaying customer segmentation breakdown
 * (TOTAL / ONLINE / OFFLINE) per service type (PPPoE / Hotspot / Static-DHCP).
 *
 * Style: SOLID icons (no gradient orb / no blur backdrop) — clean & professional.
 */

const ACCENT = {
  blue:   { hex: '#3b82f6', soft: '#eff6ff', bar: 'bg-blue-500' },
  green:  { hex: '#10b981', soft: '#ecfdf5', bar: 'bg-emerald-500' },
  red:    { hex: '#d9534f', soft: '#fef2f2', bar: 'bg-red-500' },
  violet: { hex: '#8b5cf6', soft: '#f5f3ff', bar: 'bg-violet-500' },
  orange: { hex: '#f97316', soft: '#fff7ed', bar: 'bg-orange-500' },
};

const fmtNum = (n) => Number(n || 0).toLocaleString('id-ID');

// Smoothly animate from previous value to target.
const useCountUp = (target, duration = 1200) => {
  const [val, setVal] = useState(0);
  const fromRef = useRef(0);
  const rafRef = useRef(null);
  useEffect(() => {
    const prefersReduced = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const t = Number(target) || 0;
    if (prefersReduced || duration <= 0) {
      setVal(t);
      fromRef.current = t;
      return;
    }
    const from = fromRef.current;
    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const p = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const next = Math.round(from + (t - from) * eased);
      setVal(next);
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = t;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);
  return val;
};

const AnimatedNumber = ({ value, duration = 1200, className, style, testid }) => {
  const v = useCountUp(value, duration);
  return (
    <span className={className} style={style} data-testid={testid}>{fmtNum(v)}</span>
  );
};

const surfaceFor = (variant) => {
  if (variant === 'dark') {
    return {
      card: 'bg-slate-900 border border-slate-700 text-slate-100',
      titleText: 'text-slate-400',
      valueText: 'text-white',
      rowHover: 'hover:bg-slate-800',
      rowText: 'text-slate-300',
      subText: 'text-slate-500',
      track:  'bg-slate-700',
      iconBg: 'bg-slate-800',
    };
  }
  if (variant === 'light') {
    return {
      card: 'bg-white border border-slate-200 shadow-sm',
      titleText: 'text-slate-500',
      valueText: 'text-slate-700',
      rowHover: 'hover:bg-slate-50',
      rowText: 'text-slate-700',
      subText: 'text-slate-500',
      track:  'bg-slate-200',
      iconBg: 'bg-slate-100',
    };
  }
  return {
    card: 'bg-white/80 border border-white shadow-sm',
    titleText: 'text-slate-500',
    valueText: 'text-slate-700',
    rowHover: 'hover:bg-slate-50',
    rowText: 'text-slate-700',
    subText: 'text-slate-500',
    track:  'bg-slate-200',
    iconBg: 'bg-slate-100',
  };
};

const BreakdownRow = ({ icon: Icon, label, value, total, accent, to, surface, testid }) => {
  const a = ACCENT[accent] || ACCENT.blue;
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  const Wrapper = to ? Link : 'div';
  const wrapperProps = to ? { to } : {};
  return (
    <Wrapper
      {...wrapperProps}
      data-testid={testid}
      className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${surface.rowHover} ${to ? 'cursor-pointer' : ''}`}
    >
      <span
        className="w-2 h-2 rounded-full flex-shrink-0"
        style={{
          backgroundColor: a.hex,
          boxShadow: `0 0 0 3px ${a.hex}1f`,
        }}
        aria-hidden="true"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className={`text-xs font-medium ${surface.rowText} truncate`}>{label}</span>
          <AnimatedNumber
            value={value}
            duration={1100}
            className={`text-sm font-semibold ${surface.valueText} tabular-nums`}
          />
        </div>
        <div className={`relative h-1.5 w-full rounded-full ${surface.track}`}>
          <div
            className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 bar-stripes ${a.bar}`}
            style={{ width: `${pct}%` }}
          >
            {pct > 0 && (
              <span
                className="bar-glow absolute right-0 top-1/2 w-1.5 h-1.5 rounded-full"
                style={{
                  backgroundColor: '#ffffff',
                  boxShadow: `0 0 6px ${a.hex}, 0 0 12px ${a.hex}, 0 0 18px ${a.hex}`,
                }}
              />
            )}
          </div>
        </div>
      </div>
      {to && (
        <ArrowUpRight
          className={`w-3.5 h-3.5 ${surface.subText} opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0`}
        />
      )}
    </Wrapper>
  );
};

const SegmentCard = ({ title, total, icon: Icon, accentMain, rows, surface, testid }) => {
  const a = ACCENT[accentMain] || ACCENT.blue;
  return (
    <div
      data-testid={testid}
      className={`relative rounded-2xl p-5 transition-shadow hover:shadow-md ${surface.card}`}
    >
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: `${a.hex}14`,
              color: a.hex,
              boxShadow: `inset 0 0 0 1px ${a.hex}26`,
            }}
          >
            <Icon className="w-4 h-4" strokeWidth={2} />
          </div>
          <div className={`text-[11px] font-semibold uppercase tracking-wider ${surface.titleText}`}>{title}</div>
        </div>
        <AnimatedNumber
          value={total}
          duration={1200}
          testid={`${testid}-value`}
          className={`text-3xl sm:text-4xl font-semibold tabular-nums ${surface.valueText} block`}
          style={{ letterSpacing: '-0.03em' }}
        />
      </div>

      <div className="space-y-1">
        {rows.map((r, i) => (
          <BreakdownRow key={i} {...r} surface={surface} />
        ))}
      </div>
    </div>
  );
};

const CustomerSegmentCards = ({ stats = {}, variant = 'glass' }) => {
  const s = stats || {};
  const surface = surfaceFor(variant);

  const total = {
    all: s.segment_total_all ?? (s.pppoe_total || 0) + (s.hotspot_total || 0) + (s.static_total || 0),
    pppoe: s.pppoe_total || 0,
    hotspot: s.hotspot_total || 0,
    static: s.static_total || 0,
  };
  const online = {
    all: s.segment_online_all ?? (s.pppoe_online || 0) + (s.hotspot_online || 0) + (s.static_online || 0),
    pppoe: s.pppoe_online || 0,
    hotspot: s.hotspot_online || 0,
    static: s.static_online || 0,
  };
  const offline = {
    all: s.segment_offline_all ?? Math.max(0, total.all - online.all),
    pppoe: s.pppoe_offline ?? Math.max(0, total.pppoe - online.pppoe),
    hotspot: s.hotspot_offline ?? Math.max(0, total.hotspot - online.hotspot),
    static: s.static_offline ?? Math.max(0, total.static - online.static),
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4" data-testid="customer-segment-cards">
      <SegmentCard
        testid="segment-card-total"
        title="Total Pelanggan"
        total={total.all}
        icon={UsersRound}
        accentMain="blue"
        surface={surface}
        rows={[
          { testid: 'seg-total-pppoe',   icon: Wifi,       label: 'PPPoE',         value: total.pppoe,   total: total.all, accent: 'blue',   to: '/app/customers/pppoe' },
          { testid: 'seg-total-hotspot', icon: RadioTower, label: 'Hotspot',       value: total.hotspot, total: total.all, accent: 'violet', to: '/app/customers/hotspot' },
          { testid: 'seg-total-static',  icon: Router,     label: 'Static / DHCP', value: total.static,  total: total.all, accent: 'orange', to: '/app/customers/dhcp' },
        ]}
      />
      <SegmentCard
        testid="segment-card-online"
        title="Pelanggan Online"
        total={online.all}
        icon={Radio}
        accentMain="green"
        surface={surface}
        rows={[
          { testid: 'seg-online-pppoe',   icon: Wifi,       label: 'PPPoE',         value: online.pppoe,   total: total.pppoe,   accent: 'green', to: '/app/customers/pppoe?status=online' },
          { testid: 'seg-online-hotspot', icon: RadioTower, label: 'Hotspot',       value: online.hotspot, total: total.hotspot, accent: 'green', to: '/app/hotspot-aktif' },
          { testid: 'seg-online-static',  icon: Router,     label: 'Static / DHCP', value: online.static,  total: total.static,  accent: 'green', to: '/app/customers/dhcp?status=online' },
        ]}
      />
      <SegmentCard
        testid="segment-card-offline"
        title="Pelanggan Offline"
        total={offline.all}
        icon={Unplug}
        accentMain="red"
        surface={surface}
        rows={[
          { testid: 'seg-offline-pppoe',   icon: Wifi,       label: 'PPPoE',         value: offline.pppoe,   total: total.pppoe,   accent: 'red', to: '/app/customers/pppoe?status=offline' },
          { testid: 'seg-offline-hotspot', icon: RadioTower, label: 'Hotspot',       value: offline.hotspot, total: total.hotspot, accent: 'red', to: '/app/customers/hotspot?status=offline' },
          { testid: 'seg-offline-static',  icon: Router,     label: 'Static / DHCP', value: offline.static,  total: total.static,  accent: 'red', to: '/app/customers/dhcp?status=offline' },
        ]}
      />
    </div>
  );
};


// ============== SECONDARY KPI ROW ==============
// Smaller 3-card row for financial / status KPIs (Belum Bayar / Jatuh Tempo / Diisolir).
// Style consistent with SegmentCard: solid icon, font-semibold, slate-700, no gradient orb.

const SECONDARY_DEFS = [
  { key: 'pending', testid: 'kpi-belum-bayar', title: 'Belum Bayar', subtitle: 'Invoice pending', icon: Receipt,   accent: 'orange', to: '/app/invoices?status=pending' },
  { key: 'due',     testid: 'kpi-jatuh-tempo', title: 'Jatuh Tempo', subtitle: 'Perlu ditagih',   icon: CalendarClock,      accent: 'yellow', to: '/app/invoices?status=overdue' },
  { key: 'isolir',  testid: 'kpi-isolir',      title: 'Diisolir',    subtitle: 'Layanan disable', icon: Ban, accent: 'red',   to: '/app/customers?isolir=isolated' },
];

const SECONDARY_ACCENT = {
  orange: { hex: '#f97316' },
  yellow: { hex: '#eab308' },
  red:    { hex: '#d9534f' },
};

const MiniKPI = ({ def, value, surface }) => {
  const a = SECONDARY_ACCENT[def.accent] || SECONDARY_ACCENT.orange;
  const Icon = def.icon;
  const Wrapper = def.to ? Link : 'div';
  const wrapperProps = def.to ? { to: def.to } : {};
  return (
    <Wrapper
      {...wrapperProps}
      data-testid={def.testid}
      className={`relative rounded-2xl p-4 transition-shadow hover:shadow-md ${surface.card} ${def.to ? 'cursor-pointer' : ''} block`}
    >
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: `${a.hex}14`,
              color: a.hex,
              boxShadow: `inset 0 0 0 1px ${a.hex}26`,
            }}
          >
            <Icon className="w-4 h-4" strokeWidth={2} />
          </div>
          <div className={`text-[11px] font-semibold uppercase tracking-wider ${surface.titleText}`}>{def.title}</div>
        </div>
        <AnimatedNumber
          value={value}
          duration={1100}
          className={`text-2xl sm:text-3xl font-semibold tabular-nums ${surface.valueText} block`}
          style={{ letterSpacing: '-0.02em' }}
        />
        <div className={`text-[11px] mt-0.5 ${surface.subText}`}>{def.subtitle}</div>
      </div>
    </Wrapper>
  );
};

export const SecondaryKPIRow = ({ pendingInvoices = 0, dueCustomers = 0, isolated = 0, variant = 'glass' }) => {
  const surface = surfaceFor(variant);
  const values = { pending: pendingInvoices, due: dueCustomers, isolir: isolated };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4" data-testid="secondary-kpi-row">
      {SECONDARY_DEFS.map(def => (
        <MiniKPI key={def.key} def={def} value={values[def.key]} surface={surface} />
      ))}
    </div>
  );
};

/**
 * VoucherKPIRow — 5 cards: Total/Today/Login/Expired voucher + Voucher Online (live from radacct).
 * 2026-02-03: Tambah card "Voucher Online" (fetch self-contained ke /api/vouchers?status=active_online).
 *             Fix path /app/vouchers (404) → /app/manajemen-voucher.
 */
export const VoucherKPIRow = ({ stats = {} }) => {
  const [onlineCount, setOnlineCount] = useState(0);

  // Fetch voucher online count with light polling (60s) — reuse endpoint yang sudah ada.
  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    let stopped = false;
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API_URL}/api/vouchers?status=active_online&limit=1`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        });
        if (!stopped && res.data?.success) {
          setOnlineCount(Number(res.data.total || 0));
        }
      } catch {
        /* silent fail — dashboard tetap render tanpa angka */
      }
    };
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { stopped = true; clearInterval(t); };
  }, []);

  const cards = [
    { key: 'total',   label: 'Voucher Tersedia', value: stats.vouchers_available ?? stats.total_vouchers ?? 0, to: '/app/manajemen-voucher?status=available',     badge: 'bg-violet-500',  badgeRing: 'ring-violet-100',  Icon: IdCard },
    { key: 'today',   label: 'VC Created Today', value: stats.vouchers_created_today || 0,                    to: '/app/manajemen-voucher?date=today',           badge: 'bg-emerald-500', badgeRing: 'ring-emerald-100', Icon: Printer },
    { key: 'login',   label: 'VC Login Today',   value: stats.vouchers_login_today || 0,                      to: '/app/manajemen-voucher?status=used',          badge: 'bg-sky-500',     badgeRing: 'ring-sky-100',     Icon: LogIn },
    { key: 'online',  label: 'Voucher Online',   value: onlineCount,                                          to: '/app/manajemen-voucher?status=active_online', badge: 'bg-green-500',   badgeRing: 'ring-green-100',   Icon: Wifi },
    { key: 'expired', label: 'Exp Voucher',      value: stats.vouchers_expired || 0,                          to: '/app/manajemen-voucher?status=expired',       badge: 'bg-rose-500',    badgeRing: 'ring-rose-100',    Icon: CalendarX2 },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4" data-testid="voucher-kpi-row">
      {cards.map(c => (
        <Link to={c.to} key={c.key} className="group rounded-xl px-3.5 py-3 bg-white dark:bg-slate-800/50 border border-slate-200/70 dark:border-slate-700/50 shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-slate-300 dark:hover:border-slate-600 transition-all cursor-pointer block" data-testid={`voucher-kpi-${c.key}`}>
          <div className="flex items-center gap-2 mb-1">
            <div className={`shrink-0 w-7 h-7 rounded-md ${c.badge} flex items-center justify-center group-hover:scale-110 transition-transform`}>
              <c.Icon className="text-white w-3.5 h-3.5" strokeWidth={2.25} />
            </div>
            <div className="text-[10px] uppercase font-medium tracking-wider text-slate-500 dark:text-slate-400 truncate">{c.label}</div>
          </div>
          <div className="text-lg sm:text-xl font-semibold tabular-nums text-slate-800 dark:text-slate-100 leading-tight">{Number(c.value).toLocaleString('id-ID')}</div>
        </Link>
      ))}
    </div>
  );
};

export default CustomerSegmentCards;
